import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";
import useModuleAccess from "../hooks/useModuleAccess";

const API_URL = process.env.REACT_APP_API_URL;

const CustomerStatCardSkeleton = () => (
  <div className="col-xl-auto col-lg-auto col-md-4 mb-2 stat-card-col">
    <div className="card supplier-stat-card">
      <div className="card-body">
        <div className="stat-skeleton stat-skeleton-title" />
        <div className="stat-skeleton stat-skeleton-value" />
      </div>
    </div>
  </div>
);

function parseApiResponse(raw) {
  let d = raw;
  if (typeof d === "string") {
    try {
      d = JSON.parse(d);
    } catch {
      return raw;
    }
  }
  if (d && d.body != null) {
    if (typeof d.body === "string") {
      try {
        return JSON.parse(d.body);
      } catch {
        return d.body;
      }
    }
    return d.body;
  }
  return d;
}

function parseJsonIfString(value) {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

/** API Gateway / Lambda sometimes returns `data` as a JSON string. */
function normalizeApiPayload(raw) {
  let obj = raw;
  if (!obj || typeof obj !== "object") return raw;
  obj = { ...obj };
  if (typeof obj.data === "string") {
    obj.data = parseJsonIfString(obj.data);
  }
  if (
    obj.data &&
    typeof obj.data === "object" &&
    typeof obj.data.data === "string"
  ) {
    obj.data = { ...obj.data, ...parseJsonIfString(obj.data.data) };
  }
  return obj;
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== "object") return false;
  const s = responseData.success;
  return s === true || s === "true" || s === 1 || s === "1";
}

/** Resolve base64 xlsx string from export API (direct, API Gateway body, or nested JSON). */
function extractExcelBase64(raw) {
  if (raw == null) return null;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (t.startsWith("UEs")) return raw;
    if (t.startsWith("{")) {
      try {
        return extractExcelBase64(JSON.parse(t));
      } catch {
        return null;
      }
    }
    return raw;
  }
  if (typeof raw === "object") {
    if (raw.body != null) return extractExcelBase64(raw.body);
    if (typeof raw.data === "string") return extractExcelBase64(raw.data);
    if (raw.data != null && typeof raw.data === "object") {
      const nested = extractExcelBase64(raw.data);
      if (nested) return nested;
    }
    if (raw.file != null) return extractExcelBase64(raw.file);
    if (raw.base64 != null) return extractExcelBase64(raw.base64);
  }
  return null;
}

function triggerXlsxDownloadFromBase64(base64, filenamePrefix = "customers-export") {
  if (!base64 || typeof base64 !== "string") {
    throw new Error("Invalid Excel data received");
  }
  const cleaned = base64.trim();
  if (!cleaned.startsWith("UEs")) {
    throw new Error("Invalid Excel data received");
  }
  const byteCharacters = atob(cleaned);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, -5);
  link.download = `${filenamePrefix}-${timestamp}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

function extractCustomersPayload(responseData) {
  const root = responseData;
  let dataBlock = root?.data != null ? root.data : root;

  if (typeof dataBlock === "string") {
    dataBlock = parseJsonIfString(dataBlock);
  }
  if (!dataBlock || typeof dataBlock !== "object") {
    return { customers: [], pagination: {} };
  }

  /** `data` is sometimes a bare array of customers */
  if (Array.isArray(dataBlock)) {
    return {
      customers: dataBlock,
      pagination:
        root.pagination && typeof root.pagination === "object"
          ? root.pagination
          : {},
    };
  }

  let list =
    dataBlock.customers ||
    dataBlock.customerList ||
    dataBlock.items ||
    root.customers;

  if (list == null && dataBlock.data != null) {
    const inner =
      typeof dataBlock.data === "string"
        ? parseJsonIfString(dataBlock.data)
        : dataBlock.data;
    if (inner && typeof inner === "object") {
      list = inner.customers || inner.customerList || inner.items;
    }
  }

  /**
   * Backend may return one customer document inside `data` (not `data.customers`).
   * Example: { success, data: { _id, customerName, contacts: [...] } }
   */
  if (
    (list == null || (Array.isArray(list) && list.length === 0)) &&
    dataBlock.customers == null &&
    dataBlock.customerList == null &&
    dataBlock.items == null &&
    (dataBlock._id != null || dataBlock.customerName != null)
  ) {
    list = [dataBlock];
  }

  const pagination =
    (dataBlock && dataBlock.pagination) || root.pagination || {};

  if (typeof list === "string") {
    list = parseJsonIfString(list);
  }

  return {
    customers: Array.isArray(list) ? list : [],
    pagination:
      pagination && typeof pagination === "object" && !Array.isArray(pagination)
        ? pagination
        : {},
  };
}

const primaryContact = (customer) => {
  const list = customer?.contacts;
  if (!Array.isArray(list) || list.length === 0) return {};
  return list[0];
};

const CustomerMaster = () => {
  const navigate = useNavigate();
  const { canCreate, canView, canUpdate, canDelete, user } =
    useModuleAccess("customer_master");
  const savedFilters = JSON.parse(
    sessionStorage.getItem("customerMasterFilters") || "{}",
  );
  const userRole = user?.role || localStorage.getItem("role");

  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteCustomerId, setDeleteCustomerId] = useState(null);
  const [deleteCustomerName, setDeleteCustomerName] = useState("");
  const [searchQuery, setSearchQuery] = useState(
    savedFilters.searchQuery || "",
  );
  const [activeSearchQuery, setActiveSearchQuery] = useState(
    savedFilters.activeSearchQuery || "",
  );
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(savedFilters.currentPage || 1);
  const [itemsPerPage, setItemsPerPage] = useState(
    savedFilters.itemsPerPage || 25,
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [isCustomerStatusLoading, setIsCustomerStatusLoading] = useState(true);

  const [customerStatus, setCustomerStatus] = useState({
    totalCustomers: 0,
    activeCustomers: 0,
    inactiveCustomers: 0,
  });

  const handleSort = (field) => {
    if (sortBy === field) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else {
        setSortBy("createdAt");
        setSortOrder("desc");
      }
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }

    setCurrentPage(1);
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) {
      return (
        <i
          className="fa-solid fa-sort text-muted ml-1"
          style={{ fontSize: "0.8em", flexShrink: 0 }}
        />
      );
    }

    if (sortOrder === "asc") {
      return (
        <i
          className="fa-solid fa-sort-up text-primary ml-1"
          style={{ fontSize: "0.8em", flexShrink: 0 }}
        />
      );
    }

    return (
      <i
        className="fa-solid fa-sort-down text-primary ml-1"
        style={{ fontSize: "0.8em", flexShrink: 0 }}
      />
    );
  };

  const fetchCustomers = useCallback(async () => {
    setIsLoading(true);
    try {
      const payload = {
        action: "get-all-customers",
        page: currentPage,
        limit: itemsPerPage,
        search: activeSearchQuery ? activeSearchQuery.trim() : "",
        sortBy,
        sortOrder,
      };

      const response = await axios.post(API_URL, payload);
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      const { customers: list, pagination } =
        extractCustomersPayload(responseData);

      const ok =
        isApiSuccess(responseData) || (Array.isArray(list) && list.length > 0);

      if (ok) {
        setCustomers(list);
        const total =
          typeof pagination.total === "number" ? pagination.total : list.length;
        setTotalCount(total);
      } else {
        toast.error("Failed to fetch customers", { autoClose: 2000 });
        setCustomers([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Fetch customers error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      const msg =
        typeof errData === "object" && errData?.message
          ? errData.message
          : "An error occurred while fetching customers";
      toast.error(msg, { autoClose: 2000 });
      setCustomers([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, itemsPerPage, activeSearchQuery, sortBy, sortOrder]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage) || 1);
  const startItem =
    customers.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endItem = Math.min(currentPage * itemsPerPage, totalCount);

  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value, 10);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const fetchCustomerStatus = async () => {
    try {
      const payload = {
        action: "customer-counts",
      };

      const response = await axios.post(API_URL, payload);

      let responseData = response.data;

      if (typeof responseData === "string") {
        responseData = JSON.parse(responseData);
      }

      if (responseData.success) {
        setCustomerStatus({
          totalCustomers: responseData.data.totalCount || 0,
          activeCustomers: responseData.data.activeCount || 0,
          inactiveCustomers: responseData.data.inactiveCount || 0,
        });
      }
    } catch (error) {
      console.error("Customer stats error:", error);
    } finally {
      setIsCustomerStatusLoading(false);
    }
  };
  useEffect(() => {
    fetchCustomerStatus();
  }, []);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);

      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }, 100);
    }
  };
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7;
    const tp = totalPages;

    if (tp <= maxVisiblePages) {
      for (let i = 1; i <= tp; i++) pages.push(i);
    } else if (currentPage <= 4) {
      for (let i = 1; i <= 5; i++) pages.push(i);
      pages.push("ellipsis");
      pages.push(tp);
    } else if (currentPage >= tp - 3) {
      pages.push(1);
      pages.push("ellipsis");
      for (let i = tp - 4; i <= tp; i++) pages.push(i);
    } else {
      pages.push(1);
      pages.push("ellipsis");
      for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
      pages.push("ellipsis");
      pages.push(tp);
    }
    return pages;
  };

  useEffect(() => {
    const filters = {
      searchQuery,
      activeSearchQuery,
      currentPage,
      itemsPerPage,
    };
    sessionStorage.setItem("customerMasterFilters", JSON.stringify(filters));
  }, [searchQuery, activeSearchQuery, currentPage, itemsPerPage]);

  const handleSearchChange = (e) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);
    const trimmedValue = newValue ? newValue.trim() : "";
    if (trimmedValue === "" && activeSearchQuery !== "") {
      setActiveSearchQuery("");
      if (currentPage !== 1) setCurrentPage(1);
    }
  };

  const handleSearchTrigger = () => {
    const trimmedQuery = searchQuery ? searchQuery.trim() : "";
    if (trimmedQuery === "") {
      setActiveSearchQuery("");
      if (currentPage !== 1) setCurrentPage(1);
    } else {
      setActiveSearchQuery(trimmedQuery);
      if (currentPage !== 1) setCurrentPage(1);
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearchTrigger();
    }
  };

  const handleDeleteClick = (id, name) => {
    setDeleteCustomerId(id);
    setDeleteCustomerName(name || "");
  };

  const handleDelete = async () => {
    if (!deleteCustomerId) return;

    const requestUserId = localStorage.getItem("id");
    if (!requestUserId) {
      toast.error("User session expired. Please login again.", {
        autoClose: 2000,
      });
      setTimeout(() => navigate("/"), 2000);
      return;
    }

    setIsDeleting(true);
    try {
      const payload = {
        action: "delete-customer",
        id: deleteCustomerId,
        requestUserId,
      };

      const response = await axios.post(API_URL, payload);
      const responseData = normalizeApiPayload(parseApiResponse(response.data));

      if (isApiSuccess(responseData)) {
        toast.success(responseData.message || "Customer deleted successfully", {
          autoClose: 2000,
        });
        if (window.$) {
          window.$("#modaldeleteCustomer").modal("hide");
        }
        setDeleteCustomerId(null);
        setDeleteCustomerName("");
        if (customers.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchCustomers();
        }
      } else {
        toast.error(responseData?.message || "Failed to delete customer", {
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Delete customer error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      const msg =
        typeof errData === "object" && errData?.message
          ? errData.message
          : "An error occurred while deleting customer";
      toast.error(msg, { autoClose: 2000 });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleExportCustomers = async () => {
    const currentUserId = localStorage.getItem("id");
    if (!currentUserId) {
      toast.error("User session expired. Please login again.", {
        autoClose: 2000,
      });
      setTimeout(() => navigate("/"), 2000);
      return;
    }

    setIsExporting(true);
    try {
      const payload = {
        action: "export-customers",
        currentUserId,
        search: activeSearchQuery ? activeSearchQuery.trim() : "",
        status: "active",
      };

      const response = await axios.post(API_URL, payload);
      const base64 = extractExcelBase64(response.data);

      if (!base64) {
        const responseData = normalizeApiPayload(parseApiResponse(response.data));
        throw new Error(responseData?.message || "Invalid Excel data received");
      }

      triggerXlsxDownloadFromBase64(base64, "customers-export");
      toast.success("Export completed successfully", { autoClose: 2000 });
    } catch (error) {
      console.error("Export customers error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      const msg =
        (typeof errData === "object" && errData?.message) ||
        error?.message ||
        "An error occurred while exporting customers";
      toast.error(msg, { autoClose: 2000 });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1 className="m-0 txt_lft">Customer Master</h1>
            </div>
            <div className="col-sm-6 txt_rgt">
              <button
                type="button"
                className="btn btn-success mr-2"
                onClick={handleExportCustomers}
                disabled={isExporting}
              >
                <i
                  className={`fa-solid ${isExporting ? "fa-spinner fa-spin" : "fa-file-export"} mr-1`}
                />
                {isExporting ? "Exporting..." : "Export"}
              </button>
              {canCreate && (
              <Link to="/customerMasterAdd" className="btn btn-blue mr-2">
                Add New
              </Link>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="row stat-card-row">
                {isCustomerStatusLoading ? (
                  <>
                    <CustomerStatCardSkeleton />
                    <CustomerStatCardSkeleton />
                    <CustomerStatCardSkeleton />
                  </>
                ) : (
                  <>
                    {/* Total Customers */}
                    <div className="col-xl-auto col-lg-auto col-md-4 mb-2 stat-card-col">
                      <div className="card supplier-stat-card">
                        <div className="card-body">
                          <div
                            className="stat-title"
                            style={{
                              color: "#127aea",
                              fontSize: "13px",
                              fontWeight: "600",
                              marginBottom: "8px",
                            }}
                          >
                            Total Customers
                          </div>

                          <div
                            className="stat-value"
                            style={{
                              color: "#2c7fd8",
                              fontSize: "22px",
                              fontWeight: "700",
                            }}
                          >
                            {customerStatus.totalCustomers}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Active Customers */}
                    <div className="col-xl-auto col-lg-auto col-md-4 mb-2 stat-card-col">
                      <div className="card supplier-stat-card">
                        <div className="card-body">
                          <div
                            className="stat-title"
                            style={{
                              color: "#196F3D",
                              fontSize: "13px",
                              fontWeight: "600",
                              marginBottom: "8px",
                            }}
                          >
                            Active Customers
                          </div>

                          <div
                            className="stat-value"
                            style={{
                              color: "#27AE60",
                              fontSize: "22px",
                              fontWeight: "700",
                            }}
                          >
                            {customerStatus.activeCustomers}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Inactive Customers */}
                    <div className="col-xl-auto col-lg-auto col-md-4 mb-2 stat-card-col">
                      <div className="card supplier-stat-card">
                        <div className="card-body">
                          <div
                            className="stat-title"
                            style={{
                              color: "#424949",
                              fontSize: "13px",
                              fontWeight: "600",
                              marginBottom: "8px",
                            }}
                          >
                            Inactive Customers
                          </div>

                          <div
                            className="stat-value"
                            style={{
                              color: "#7F8C8D",
                              fontSize: "22px",
                              fontWeight: "700",
                            }}
                          >
                            {customerStatus.inactiveCustomers}
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div>
                <div className="card-body">
                  <div className="row align-items-center">
                    <div className="col-md-6">
                      <div className="form-group mb-0">
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search customers..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            onKeyPress={handleSearchKeyPress}
                          />
                          <div className="input-group-append">
                            <div
                              className="input-group-text crsr_pntr"
                              onClick={handleSearchTrigger}
                              style={{ cursor: "pointer" }}
                            >
                              <i className="fa-solid fa-search" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6 text-right">
                      <div className="form-group mb-0">
                        <p className="mb-0">
                          {activeSearchQuery && (
                            <span className="ml-2 text-muted">
                              (Showing: {customers.length})
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {isLoading ? (
                <div className="text-center p-4">
                  <PulseLoader color="#1199B2" size={10} />
                </div>
              ) : customers.length === 0 && !activeSearchQuery ? (
                <div className="nocontent_div text-center p-4">
                  There are no customers yet.
                  <br />
                  <div className="mt-3">
                    {canCreate && (
                    <Link to="/customerMasterAdd" className="btn btn-blue">
                      Add New Customer
                    </Link>
                    )}
                  </div>
                </div>
              ) : customers.length === 0 && activeSearchQuery ? (
                <div className="nocontent_div text-center p-4">
                  No customers found matching your search.
                  <br />
                  <div className="mt-3">
                    <button
                      type="button"
                      className="btn btn-blue"
                      onClick={() => {
                        setSearchQuery("");
                        setActiveSearchQuery("");
                        setCurrentPage(1);
                      }}
                    >
                      Clear Search
                    </button>
                  </div>
                </div>
              ) : (
                <div className="supply-table-container">
                  <div className="table-responsive">
                    <table
                      id="customers"
                      width="100%"
                      className="table table-striped table-bordered txt_lft"
                    >
                      <thead>
                        <tr>
                          <th
                            style={{
                              whiteSpace: "nowrap",
                              width: "40px",
                              minWidth: "40px",
                            }}
                          >
                            S.No
                          </th>
                          <th
                            onClick={() => handleSort("customerName")}
                            style={{
                              whiteSpace: "nowrap",
                              width: "150px",
                              minWidth: "150px",
                            }}
                          >
                            Customer Name {getSortIcon("customerName")}
                          </th>
                          <th
                            onClick={() => handleSort("contactPerson")}
                            style={{ width: "100px", minWidth: "100px" }}
                          >
                            Contact Name {getSortIcon("contactPerson")}
                          </th>
                          <th
                            onClick={() => handleSort("email")}
                            style={{
                              width: "190px",
                              minWidth: "190px",
                              maxWidth: "190px",
                              wordWrap: "break-word",
                              wordBreak: "break-word",
                              whiteSpace: "normal",
                            }}
                          >
                            Email {getSortIcon("email")}
                          </th>
                          <th style={{ width: "90px", minWidth: "90px" }}>
                            Phone
                          </th>

                          <th
                            onClick={() => handleSort("city")}
                            style={{ width: "120px", minWidth: "120px" }}
                          >
                            City {getSortIcon("city")}
                          </th>
                          <th style={{ width: "70px", minWidth: "70px" }}>
                            Contacts{" "}
                          </th>
                          <th
                            onClick={() => handleSort("createdBy")}
                            style={{
                              whiteSpace: "nowrap",
                              width: "120px",
                              minWidth: "120px",
                            }}
                          >
                            Created By {getSortIcon("createdBy")}
                          </th>
                          {userRole === "admin" && (
                            <th
                              onClick={() => handleSort("status")}
                              style={{
                                whiteSpace: "nowrap",
                                width: "70px",
                                minWidth: "70px",
                              }}
                            >
                              Status {getSortIcon("status")}
                            </th>
                          )}
                          <th style={{ width: "100px", minWidth: "100px" }}>
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {customers.map((customer, index) => {
                          const pc = primaryContact(customer);
                          const cid = customer._id || customer.id;
                          const createdName =
                            customer.createdBy &&
                            typeof customer.createdBy === "object"
                              ? customer.createdBy.name
                              : customer.createdBy || "-";
                          const contactCount = Array.isArray(customer.contacts)
                            ? customer.contacts.length
                            : 0;
                          return (
                            <tr key={cid}>
                              <td style={{ width: "10px", minWidth: "10px" }}>
                                {(currentPage - 1) * itemsPerPage + index + 1}
                              </td>
                              <td style={{ width: "150px", minWidth: "150px" }}>
                                {customer.customerName || "-"}
                              </td>
                              <td style={{ width: "150px", minWidth: "150px" }}>
                                <div
                                  style={{
                                    width: "90px",
                                    minWidth: "90px",
                                    maxWidth: "90px",
                                    wordWrap: "break-word",
                                    wordBreak: "break-word",
                                    whiteSpace: "normal",
                                  }}
                                >
                                  {pc.contactPerson || "-"}
                                </div>
                              </td>
                              <td
                                style={{
                                  width: "190px",
                                  minWidth: "190px",
                                  maxWidth: "190px",
                                  wordWrap: "break-word",
                                  wordBreak: "break-word",
                                  whiteSpace: "normal",
                                }}
                              >
                                {pc.email || "-"}
                              </td>
                              <td style={{ width: "90px", minWidth: "90px" }}>
                                {pc.contactNumber || "-"}
                              </td>

                              <td style={{ width: "120px", minWidth: "120px" }}>
                                {pc.city || "-"}
                              </td>
                              <td style={{ width: "70px", minWidth: "70px" }}>
                                {contactCount}
                              </td>
                              <td style={{ width: "120px", minWidth: "120px" }}>
                                {createdName}
                              </td>
                              {userRole === "admin" && (
                                <td style={{ width: "70px", minWidth: "70px" }}>
                                  <span
                                    className={`fwt_600 ${
                                      customer.status === "active"
                                        ? "f_green"
                                        : "f_red"
                                    }`}
                                    style={{ fontSize: "90%", fontWeight: 550 }}
                                  >
                                    {customer.status
                                      ? customer.status
                                          .charAt(0)
                                          .toUpperCase() +
                                        customer.status.slice(1).toLowerCase()
                                      : "-"}
                                  </span>
                                </td>
                              )}
                              <td
                                style={{
                                  width: "100px",
                                  minWidth: "100px",
                                  maxWidth: "100px",
                                  wordWrap: "break-word",
                                  wordBreak: "break-word",
                                  whiteSpace: "normal",
                                }}
                              >
                                <div
                                  align="center"
                                  style={{ whiteSpace: "nowrap" }}
                                >
                                  {canView && (
                                  <Link
                                    to={`/customerMasterView/${cid}`}
                                    className="crsr_pntr"
                                    style={{ display: "inline-block" }}
                                  >
                                    <i className="fa-solid fa-eye mr-2" />
                                  </Link>
                                  )}
                                  {canUpdate && (
                                  <Link
                                    to={`/customerMasterEdit/${cid}`}
                                    className="crsr_pntr"
                                    style={{ display: "inline-block" }}
                                  >
                                    <i className="fa-solid fa-pen mr-2" />
                                  </Link>
                                  )}
                                  {canDelete && (
                                  <button
                                    type="button"
                                    className="crsr_pntr btn btn-link p-0 border-0 align-baseline"
                                    data-toggle="modal"
                                    data-target="#modaldeleteCustomer"
                                    onClick={() =>
                                      handleDeleteClick(
                                        cid,
                                        customer.customerName,
                                      )
                                    }
                                    title="Delete"
                                    style={{
                                      display: "inline-block",
                                      color: "var(--blue, #0094a9)",
                                      textDecoration: "none",
                                    }}
                                  >
                                    <i
                                      className="fa-solid fa-trash-can"
                                      style={{ color: "inherit" }}
                                      aria-hidden="true"
                                    />
                                  </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {!isLoading && customers.length > 0 && (
                    <div className="supply-pagination-container">
                      <div className="pagination-wrapper">
                        {/* Entries Section */}
                        <div className="pagination-info">
                          <label
                            className="mb-0 mr-2"
                            style={{ fontSize: "13px" }}
                          >
                            Show:
                          </label>

                          <select
                            className="form-control form-control-sm"
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                          >
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                          </select>

                          <span className="ml-2" style={{ fontSize: "13px" }}>
                            entries
                          </span>

                          <span
                            className="ml-3 text-muted"
                            style={{ fontSize: "13px" }}
                          >
                            {startItem}–{endItem} of {totalCount}
                          </span>
                        </div>

                        {/* Pagination Buttons */}
                        <nav className="pagination-nav">
                          <ul className="pagination mb-0">
                            <li
                              className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1}
                                title="First Page"
                              >
                                <i className="fa-solid fa-angle-double-left"></i>
                              </button>
                            </li>

                            <li
                              className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(currentPage - 1)
                                }
                                disabled={currentPage === 1}
                                title="Previous Page"
                              >
                                <i className="fa-solid fa-angle-left"></i>
                              </button>
                            </li>

                            {getPageNumbers().map((page, index) =>
                              page === "ellipsis" ? (
                                <li
                                  key={`ellipsis-${index}`}
                                  className="page-item disabled"
                                >
                                  <span className="page-link">...</span>
                                </li>
                              ) : (
                                <li
                                  key={page}
                                  className={`page-item ${
                                    currentPage === page ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => handlePageChange(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              ),
                            )}

                            <li
                              className={`page-item ${
                                currentPage === totalPages ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(currentPage + 1)
                                }
                                disabled={currentPage === totalPages}
                                title="Next Page"
                              >
                                <i className="fa-solid fa-angle-right"></i>
                              </button>
                            </li>

                            <li
                              className={`page-item ${
                                currentPage === totalPages ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(totalPages)}
                                disabled={currentPage === totalPages}
                                title="Last Page"
                              >
                                <i className="fa-solid fa-angle-double-right"></i>
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="modaldeleteCustomer"
        tabIndex={-1}
        aria-labelledby="modaldeleteCustomerLabel"
        aria-hidden="true"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modaldeleteCustomerLabel">
                Delete Customer
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body">
              <div className="w-100 d-inline-block text-center">
                Are you sure you want to delete <br />
                <span className="fwt_600">{deleteCustomerName}</span>?
              </div>
            </div>
            <div className="modal-footer justify-content-center">
              <button
                type="button"
                className="btn btn-secondary mr-2"
                data-dismiss="modal"
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Yes, delete"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerMaster;
